import { useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, Box, Boxes, CheckCircle2, Circle, Copy, Pencil, Plus, Users } from "lucide-react";
import { Button, Skeleton } from "@finora/ui";
import type { PackageDto, ModuleDto } from "@finora/types";
import { api } from "../../lib/api";

/**
 * Finora Admin -> Paketi. Redizajnirano po mockup slici korisnika (stat
 * kartice, pretraga/sortiranje liste, kartice paketa sa Uredi/Dupliraj, i
 * forma za novi paket koja odmah nudi i addon/minimalan-paket podešavanje po
 * modulu) - vidi razgovor "Podesavanja rasiri... deo za pakete mnogo je
 * pregledan i lep interfejs". Isti backend API kao ranije verzije stranice
 * (listPackages/createPackage/updatePackage/setPackageModule/
 * updateModuleEntitlement) - ovo je ČISTO frontend redizajn, bez izmena na
 * backendu. Finora nema poseban admin sidebar layout (za razliku od slike) -
 * stranica ostaje u istom AppShell-u kao i ostatak admina.
 */
type SortKey = "name-asc" | "name-desc" | "price-asc" | "price-desc";

function billingPeriodLabel(period: string): string {
  return period === "MONTHLY" ? "mesečno" : "godišnje";
}

export function PackagesPage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const { data: packages, isLoading: packagesLoading } = useQuery({
    queryKey: ["admin-packages"],
    queryFn: () => api.listPackages(),
  });
  const { data: modules, isLoading: modulesLoading } = useQuery({
    queryKey: ["admin-modules"],
    queryFn: () => api.listModules(),
  });

  const invalidatePackages = () => queryClient.invalidateQueries({ queryKey: ["admin-packages"] });
  const invalidateModules = () => queryClient.invalidateQueries({ queryKey: ["admin-modules"] });

  const [search, setSearch] = useState("");
  const [sort, setSort] = useState<SortKey>("name-asc");
  const [editingId, setEditingId] = useState<string | null>(null);

  const updatePackage = useMutation({
    mutationFn: (input: { id: string; payload: Partial<PackageDto> }) => api.updatePackage(input.id, input.payload),
    onSuccess: invalidatePackages,
  });

  const toggleModule = useMutation({
    mutationFn: (input: { packageId: string; moduleId: string; included: boolean }) =>
      api.setPackageModule(input.packageId, input.moduleId, input.included),
    onSuccess: invalidatePackages,
  });

  const updateEntitlement = useMutation({
    mutationFn: (input: {
      moduleId: string;
      addonAvailable?: boolean;
      addonPrice?: string | null;
      minimumPackageId?: string | null; requiredModuleKeys?: string[];
    }) => api.updateModuleEntitlement(input.moduleId, input),
    onSuccess: invalidateModules,
  });

  // "Dupliraj" ne postoji na backendu kao poseban endpoint - sastavlja se od
  // postojećih poziva (createPackage pa setPackageModule za svaki uključeni
  // modul), isti princip kao "kloniraj pa izmeni" koji admin i inače radi
  // ručno. Ključ dobija "-kopija" sufiks (i brojčani nastavak ako se sudari).
  const duplicatePackage = useMutation({
    mutationFn: async (pkg: PackageDto) => {
      const existingKeys = new Set((packages ?? []).map((p) => p.key));
      let key = `${pkg.key}-kopija`;
      let n = 2;
      while (existingKeys.has(key)) {
        key = `${pkg.key}-kopija-${n}`;
        n += 1;
      }
      const created = await api.createPackage({
        key,
        name: `${pkg.name} (kopija)`,
        price: pkg.price,
        billingPeriod: pkg.billingPeriod,
        trialEnabled: pkg.trialEnabled,
        trialDurationDays: pkg.trialDurationDays ?? undefined,
        includedUsers: pkg.includedUsers,
        additionalUserPrice: pkg.additionalUserPrice,
        additionalWarehousePrice: pkg.additionalWarehousePrice,
      });
      for (const m of pkg.includedModules) {
        await api.setPackageModule(created.id, m.id, true);
      }
      return created;
    },
    onSuccess: invalidatePackages,
  });

  const filteredPackages = useMemo(() => {
    const list = (packages ?? []).filter(
      (p) =>
        !search.trim() ||
        p.name.toLowerCase().includes(search.trim().toLowerCase()) ||
        p.key.toLowerCase().includes(search.trim().toLowerCase()),
    );
    const sorted = [...list].sort((a, b) => {
      switch (sort) {
        case "name-asc":
          return a.name.localeCompare(b.name, "sr-RS");
        case "name-desc":
          return b.name.localeCompare(a.name, "sr-RS");
        case "price-asc":
          return Number(a.price) - Number(b.price);
        case "price-desc":
          return Number(b.price) - Number(a.price);
        default:
          return 0;
      }
    });
    return sorted;
  }, [packages, search, sort]);

  const stats = useMemo(() => {
    const all = packages ?? [];
    return {
      total: all.length,
      active: all.filter((p) => p.isActive).length,
      inactive: all.filter((p) => !p.isActive).length,
      totalModules: (modules ?? []).length,
    };
  }, [packages, modules]);

  if (packagesLoading || modulesLoading) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-40 w-full" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <button
        onClick={() => navigate("/finora-admin")}
        className="flex items-center gap-1 text-sm text-slate-500 hover:text-slate-700"
      >
        <ArrowLeft className="h-4 w-4" aria-hidden />
        Nazad na HAFiskal Admin
      </button>

      <div className="flex items-start gap-3">
        <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-brand-light text-brand">
          <Boxes className="h-6 w-6" aria-hidden />
        </div>
        <div>
          <h1 className="text-xl font-semibold text-slate-900">Paketi</h1>
          <p className="text-sm text-slate-500">
            Upravljajte paketima i modulima. Odredite koje module paket uključuje, njihovu cenu i
            ograničenja.
          </p>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
        <StatCard icon={Boxes} value={stats.total} label="Ukupno paketa" iconClass="bg-brand-light text-brand" />
        <StatCard
          icon={CheckCircle2}
          value={stats.active}
          label="Aktivnih paketa"
          iconClass="bg-emerald-50 text-emerald-600"
        />
        <StatCard icon={Circle} value={stats.inactive} label="Neaktivnih paketa" iconClass="bg-slate-100 text-slate-400" />
        <StatCard icon={Box} value={stats.totalModules} label="Ukupno modula" iconClass="bg-amber-50 text-amber-600" />
      </div>

      <div className="rounded-xl border border-border bg-surface shadow-subtle">
        <div className="flex flex-wrap items-center justify-between gap-3 border-b border-border p-4">
          <div>
            <h2 className="text-sm font-semibold text-slate-800">Postojeći paketi</h2>
            <p className="text-xs text-slate-400">Pregled i upravljanje postojećim paketima.</p>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Pretraži pakete..."
              className="h-9 w-48 rounded-lg border border-border px-3 text-sm focus:border-brand focus:outline-none"
            />
            <select
              value={sort}
              onChange={(e) => setSort(e.target.value as SortKey)}
              className="h-9 rounded-lg border border-border px-2 text-sm focus:border-brand focus:outline-none"
            >
              <option value="name-asc">Nazivu (A-Š)</option>
              <option value="name-desc">Nazivu (Š-A)</option>
              <option value="price-asc">Ceni (rastuće)</option>
              <option value="price-desc">Ceni (opadajuće)</option>
            </select>
          </div>
        </div>

        <div className="divide-y divide-border">
          {filteredPackages.length === 0 ? (
            <p className="p-4 text-sm text-slate-400">Nema paketa za prikaz.</p>
          ) : (
            filteredPackages.map((pkg) => (
              <PackageRow
                key={pkg.id}
                pkg={pkg}
                isEditing={editingId === pkg.id}
                onToggleEdit={() => setEditingId(editingId === pkg.id ? null : pkg.id)}
                onDuplicate={() => duplicatePackage.mutate(pkg)}
                duplicatePending={duplicatePackage.isPending}
                onUpdate={(payload) => updatePackage.mutate({ id: pkg.id, payload })}
                modules={modules ?? []}
                onToggleModule={(moduleId, included) => toggleModule.mutate({ packageId: pkg.id, moduleId, included })}
              />
            ))
          )}
        </div>
      </div>

      <div className="rounded-xl border border-border bg-surface p-4 shadow-subtle">
        <h2 className="mb-1 text-sm font-semibold text-slate-800">
          Podešavanje po modulu (addon / minimalan paket)
        </h2>
        <p className="mb-3 text-xs text-slate-400">
          Za module koji nisu uključeni u paket - da li mogu kao dodatak (addon) uz bilo koji paket, ili
          zahtevaju konkretan minimalni paket. Ovo je nezavisno od kreiranja novog paketa ispod - važi za
          module u svim postojećim paketima.
        </p>
        <div className="space-y-2">
          {(modules ?? [])
            .filter((m) => m.key !== "finora_mobile")
            .map((m) => (
              <ModuleEntitlementRow
                key={m.id}
                module={m}
                packages={packages ?? []}
                allModules={modules ?? []}
                onSave={(input) => updateEntitlement.mutate({ moduleId: m.id, ...input })}
              />
            ))}
        </div>
      </div>

      <NewPackageForm
        modules={modules ?? []}
        onCreated={() => invalidatePackages()}
        onSaveEntitlement={(input) => updateEntitlement.mutate(input)}
      />
    </div>
  );
}

function ModuleEntitlementRow({
  module,
  packages,
  allModules,
  onSave,
}: {
  module: ModuleDto;
  packages: PackageDto[];
  allModules: ModuleDto[];
  onSave: (input: { addonAvailable?: boolean; addonPrice?: string | null; minimumPackageId?: string | null; requiredModuleKeys?: string[] }) => void;
}) {
  const [addonAvailable, setAddonAvailable] = useState(module.addonAvailable);
  const [addonPrice, setAddonPrice] = useState(module.addonPrice ?? "");
  const [minimumPackageId, setMinimumPackageId] = useState(module.minimumPackage?.id ?? "");
  const [requiredKeys, setRequiredKeys] = useState<string[]>(module.requiredModuleKeys ?? []);
  const [pickRequired, setPickRequired] = useState(false);
  const toggleRequired = (key: string) => {
    const next = requiredKeys.includes(key) ? requiredKeys.filter((k) => k !== key) : [...requiredKeys, key];
    setRequiredKeys(next);
    onSave({ requiredModuleKeys: next });
  };

  return (
    <div className="grid grid-cols-1 items-center gap-2 rounded-lg bg-app-bg px-3 py-2 text-sm sm:grid-cols-4">
      <span className="font-medium text-slate-700">{module.name}</span>
      <label className="flex items-center gap-1.5 text-xs text-slate-500">
        <input
          type="checkbox"
          checked={addonAvailable}
          onChange={(e) => {
            setAddonAvailable(e.target.checked);
            onSave({ addonAvailable: e.target.checked });
          }}
        />
        Dostupno kao addon
      </label>
      <input
        value={addonPrice}
        onChange={(e) => setAddonPrice(e.target.value)}
        onBlur={() => onSave({ addonPrice: addonPrice || null })}
        placeholder="Cena addon-a"
        disabled={!addonAvailable}
        className="h-8 rounded-lg border border-border px-2 text-xs focus:border-brand focus:outline-none disabled:opacity-40"
      />
      <select
        value={minimumPackageId}
        onChange={(e) => {
          setMinimumPackageId(e.target.value);
          onSave({ minimumPackageId: e.target.value || null });
        }}
        className="h-8 rounded-lg border border-border px-2 text-xs focus:border-brand focus:outline-none"
      >
        <option value="">Nema minimalnog paketa</option>
        {packages.map((p) => (
          <option key={p.id} value={p.id}>
            Min: {p.name}
          </option>
        ))}
      </select>
      <div className="text-xs text-slate-500 sm:col-span-4">
        <button type="button" onClick={() => setPickRequired((v) => !v)} className="font-medium text-slate-600 hover:text-brand">
          Zahteva module: {requiredKeys.length ? requiredKeys.map((k) => allModules.find((m) => m.key === k)?.name ?? k).join(", ") : "nijedan"}
          {pickRequired ? " ▲" : " ▼"}
        </button>
        {pickRequired && (
          <div className="mt-1.5 flex flex-wrap gap-x-4 gap-y-1">
            {allModules
              .filter((m) => m.key !== module.key && m.key !== "finora_mobile")
              .map((m) => (
                <label key={m.id} className="flex items-center gap-1.5">
                  <input type="checkbox" checked={requiredKeys.includes(m.key)} onChange={() => toggleRequired(m.key)} />
                  {m.name}
                </label>
              ))}
          </div>
        )}
      </div>
    </div>
  );
}

function StatCard({
  icon: Icon,
  value,
  label,
  iconClass,
}: {
  icon: typeof Boxes;
  value: number;
  label: string;
  iconClass: string;
}) {
  return (
    <div className="flex items-center gap-3 rounded-xl border border-border bg-surface p-4 shadow-subtle">
      <div className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-lg ${iconClass}`}>
        <Icon className="h-5 w-5" aria-hidden />
      </div>
      <div>
        <p className="text-lg font-semibold text-slate-900">{value}</p>
        <p className="text-xs text-slate-500">{label}</p>
      </div>
    </div>
  );
}

function PackageRow({
  pkg,
  isEditing,
  onToggleEdit,
  onDuplicate,
  duplicatePending,
  onUpdate,
  modules,
  onToggleModule,
}: {
  pkg: PackageDto;
  isEditing: boolean;
  onToggleEdit: () => void;
  onDuplicate: () => void;
  duplicatePending: boolean;
  onUpdate: (payload: Partial<PackageDto>) => void;
  modules: ModuleDto[];
  onToggleModule: (moduleId: string, included: boolean) => void;
}) {
  return (
    <div className="p-4">
      <div className="flex flex-wrap items-center gap-3">
        <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-brand-light text-brand">
          <Box className="h-4 w-4" aria-hidden />
        </div>
        <div className="min-w-[10rem]">
          <div className="flex items-center gap-2">
            <span className="font-medium text-slate-900">{pkg.name}</span>
            <span
              className={`rounded-full px-2 py-0.5 text-xs font-medium ${
                pkg.isActive ? "bg-emerald-100 text-emerald-700" : "bg-slate-100 text-slate-500"
              }`}
            >
              {pkg.isActive ? "Aktivan" : "Neaktivan"}
            </span>
          </div>
          <p className="text-xs text-slate-400">{pkg.key}</p>
        </div>

        <span
          className={`rounded-full px-2 py-0.5 text-xs font-medium ${
            pkg.family === "API" ? "bg-sky-100 text-sky-700" : "bg-violet-100 text-violet-700"
          }`}
        >
          {pkg.family === "API" ? "HAFiskal API" : "HAFiskal ERP"}
        </span>

        <div className="text-sm">
          <span className="font-semibold text-slate-900">{Number(pkg.price).toLocaleString("sr-RS")} RSD</span>
          <span className="text-slate-400"> / {billingPeriodLabel(pkg.billingPeriod)}</span>
        </div>

        <div className="flex items-center gap-1 text-sm text-slate-500">
          <Users className="h-4 w-4" aria-hidden />
          {pkg.includedUsers} korisnika
        </div>
        <div className="flex items-center gap-1 text-sm text-slate-500">
          <Boxes className="h-4 w-4" aria-hidden />
          {pkg.includedModules.length} modula
        </div>

        <div className="ml-auto flex gap-2">
          <Button size="sm" variant="secondary" onClick={onToggleEdit}>
            <Pencil className="h-3.5 w-3.5" aria-hidden />
            {isEditing ? "Zatvori" : "Uredi"}
          </Button>
          <Button size="sm" variant="secondary" disabled={duplicatePending} onClick={onDuplicate}>
            <Copy className="h-3.5 w-3.5" aria-hidden />
            Dupliraj
          </Button>
          <button
            onClick={() => onUpdate({ isActive: !pkg.isActive })}
            className="rounded-lg border border-border px-3 text-xs font-medium text-slate-500 hover:bg-app-bg"
          >
            {pkg.isActive ? "Deaktiviraj" : "Aktiviraj"}
          </button>
        </div>
      </div>

      {isEditing && (
        <div className="mt-4 space-y-4 rounded-lg bg-app-bg p-4">
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-5">
            <div>
              <label className="mb-1 block text-xs text-slate-500">Naziv</label>
              <input
                defaultValue={pkg.name}
                onBlur={(e) => e.target.value !== pkg.name && onUpdate({ name: e.target.value })}
                className="h-9 w-full rounded-lg border border-border px-2 text-sm focus:border-brand focus:outline-none"
              />
            </div>
            <div>
              <label className="mb-1 block text-xs text-slate-500">Cena (RSD)</label>
              <input
                type="number"
                defaultValue={pkg.price}
                onBlur={(e) => e.target.value !== pkg.price && onUpdate({ price: e.target.value })}
                className="h-9 w-full rounded-lg border border-border px-2 text-sm focus:border-brand focus:outline-none"
              />
            </div>
            <div>
              <label className="mb-1 block text-xs text-slate-500">Uključeno korisnika</label>
              <input
                type="number"
                defaultValue={pkg.includedUsers}
                onBlur={(e) => onUpdate({ includedUsers: Number(e.target.value) })}
                className="h-9 w-full rounded-lg border border-border px-2 text-sm focus:border-brand focus:outline-none"
              />
            </div>
            <div>
              <label className="mb-1 block text-xs text-slate-500">Dodatni korisnik (RSD)</label>
              <input
                type="number"
                defaultValue={pkg.additionalUserPrice}
                onBlur={(e) => onUpdate({ additionalUserPrice: e.target.value })}
                className="h-9 w-full rounded-lg border border-border px-2 text-sm focus:border-brand focus:outline-none"
              />
            </div>
            <div>
              {/* 1 magacin je uvek besplatan (nije konfigurabilno) - ovo je
                  samo cena SVAKOG NAREDNOG magacina, vidi razgovor. */}
              <label className="mb-1 block text-xs text-slate-500">Dodatni magacin (RSD)</label>
              <input
                type="number"
                defaultValue={pkg.additionalWarehousePrice}
                onBlur={(e) => onUpdate({ additionalWarehousePrice: e.target.value })}
                className="h-9 w-full rounded-lg border border-border px-2 text-sm focus:border-brand focus:outline-none"
              />
            </div>
          </div>

          <div>
            <p className="mb-1.5 text-xs font-medium text-slate-500">Porodica paketa</p>
            <div className="flex gap-3 text-sm text-slate-600">
              <label className="flex items-center gap-1.5">
                <input
                  type="radio"
                  name={`family-${pkg.id}`}
                  checked={pkg.family === "ERP"}
                  onChange={() => onUpdate({ family: "ERP" })}
                />
                HAFiskal ERP
              </label>
              <label className="flex items-center gap-1.5">
                <input
                  type="radio"
                  name={`family-${pkg.id}`}
                  checked={pkg.family === "API"}
                  onChange={() => onUpdate({ family: "API" })}
                />
                HAFiskal API
              </label>
            </div>
          </div>

          <label className="flex items-center gap-2 text-sm text-slate-600">
            <input
              type="checkbox"
              checked={pkg.trialEnabled}
              onChange={(e) => onUpdate({ trialEnabled: e.target.checked })}
            />
            Trial
            <input
              type="number"
              defaultValue={pkg.trialDurationDays ?? ""}
              placeholder="dana"
              disabled={!pkg.trialEnabled}
              onBlur={(e) =>
                onUpdate({ trialDurationDays: e.target.value ? Number(e.target.value) : null })
              }
              className="h-8 w-24 rounded-lg border border-border px-2 text-sm focus:border-brand focus:outline-none disabled:opacity-40"
            />
          </label>

          <div>
            <p className="mb-1.5 text-xs font-medium text-slate-500">Uključeni moduli</p>
            <div className="flex flex-wrap gap-1.5">
              {modules
                .filter((m) => m.key !== "finora_mobile")
                .map((m) => {
                  const included = pkg.includedModules.some((im) => im.id === m.id);
                  return (
                    <button
                      key={m.id}
                      onClick={() => onToggleModule(m.id, !included)}
                      className={`rounded-full px-3 py-1 text-xs font-medium ${
                        included
                          ? "bg-emerald-100 text-emerald-700"
                          : "bg-slate-100 text-slate-400 hover:bg-slate-200"
                      }`}
                    >
                      {m.name}
                    </button>
                  );
                })}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

/**
 * Kreiranje novog paketa - odmah nudi i podešavanje addon/minimalan-paket po
 * modulu (isto kao ranija posebna sekcija "Podešavanje po modulu", samo sad
 * spojeno na jedno mesto kao na mockup slici). Modul-nivo podešavanja
 * (addon/minimumPackage) su GLOBALNA (ne vezana za paket koji se tek pravi) -
 * čuvaju se odmah na klik/blur preko updateModuleEntitlement, nezavisno od
 * toga da li je novi paket još sačuvan.
 */
function NewPackageForm({
  modules,
  onCreated,
  onSaveEntitlement,
}: {
  modules: ModuleDto[];
  onCreated: () => void;
  onSaveEntitlement: (input: {
    moduleId: string;
    addonAvailable?: boolean;
    addonPrice?: string | null;
    minimumPackageId?: string | null; requiredModuleKeys?: string[];
  }) => void;
}) {
  const [key, setKey] = useState("");
  const [name, setName] = useState("");
  const [price, setPrice] = useState("");
  const [description, setDescription] = useState("");
  const [family, setFamily] = useState<"ERP" | "API">("ERP");
  const [checkedModuleIds, setCheckedModuleIds] = useState<Set<string>>(new Set());
  const [moduleSearch, setModuleSearch] = useState("");

  const create = useMutation({
    mutationFn: async () => {
      const created = await api.createPackage({ key, name, price: price || "0", family });
      for (const moduleId of checkedModuleIds) {
        await api.setPackageModule(created.id, moduleId, true);
      }
      return created;
    },
    onSuccess: () => {
      onCreated();
      setKey("");
      setName("");
      setPrice("");
      setDescription("");
      setFamily("ERP");
      setCheckedModuleIds(new Set());
    },
  });

  const visibleModules = modules
    .filter((m) => m.key !== "finora_mobile")
    .filter((m) => !moduleSearch.trim() || m.name.toLowerCase().includes(moduleSearch.trim().toLowerCase()));

  return (
    <div className="rounded-xl border border-border bg-surface p-5 shadow-subtle">
      <div className="mb-4 flex items-start gap-3">
        <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-brand-light text-brand">
          <Boxes className="h-4 w-4" aria-hidden />
        </div>
        <div>
          <h2 className="text-sm font-semibold text-slate-800">Kreiraj novi paket</h2>
          <p className="text-xs text-slate-400">
            Unesite osnovne informacije o paketu, odaberite module i podesite ograničenja.
          </p>
        </div>
      </div>

      <div className="mb-4 grid grid-cols-1 gap-3 sm:grid-cols-4">
        <div>
          <label className="mb-1 block text-xs font-medium text-slate-500">Naziv paketa *</label>
          <input
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="npr. Starter"
            className="h-9 w-full rounded-lg border border-border px-2 text-sm focus:border-brand focus:outline-none"
          />
        </div>
        <div>
          <label className="mb-1 block text-xs font-medium text-slate-500">Ključ</label>
          <input
            value={key}
            onChange={(e) => setKey(e.target.value)}
            placeholder="npr. starter"
            className="h-9 w-full rounded-lg border border-border px-2 text-sm focus:border-brand focus:outline-none"
          />
        </div>
        <div>
          <label className="mb-1 block text-xs font-medium text-slate-500">Cena (RSD / mesečno) *</label>
          <input
            value={price}
            onChange={(e) => setPrice(e.target.value)}
            placeholder="npr. 890"
            className="h-9 w-full rounded-lg border border-border px-2 text-sm focus:border-brand focus:outline-none"
          />
        </div>
        <div>
          <label className="mb-1 block text-xs font-medium text-slate-500">Opis</label>
          <input
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="Kratak opis paketa..."
            className="h-9 w-full rounded-lg border border-border px-2 text-sm focus:border-brand focus:outline-none"
          />
        </div>
        <div>
          <label className="mb-1 block text-xs font-medium text-slate-500">Porodica paketa</label>
          <div className="flex h-9 items-center gap-3 text-sm text-slate-600">
            <label className="flex items-center gap-1.5">
              <input type="radio" name="new-package-family" checked={family === "ERP"} onChange={() => setFamily("ERP")} />
              HAFiskal ERP
            </label>
            <label className="flex items-center gap-1.5">
              <input type="radio" name="new-package-family" checked={family === "API"} onChange={() => setFamily("API")} />
              HAFiskal API
            </label>
          </div>
        </div>
      </div>

      <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
        <div>
          <p className="text-sm font-medium text-slate-700">Dostupni moduli</p>
          <p className="text-xs text-slate-400">Označite module koje želite da uključite u ovaj paket.</p>
        </div>
        <div className="flex items-center gap-2">
          <input
            value={moduleSearch}
            onChange={(e) => setModuleSearch(e.target.value)}
            placeholder="Pretraži module..."
            className="h-9 w-44 rounded-lg border border-border px-2 text-sm focus:border-brand focus:outline-none"
          />
          <button
            type="button"
            onClick={() => setCheckedModuleIds(new Set(visibleModules.map((m) => m.id)))}
            className="whitespace-nowrap rounded-lg border border-border px-3 py-2 text-xs font-medium text-brand hover:bg-brand-light"
          >
            Označi sve
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
        {visibleModules.map((m) => (
          <NewPackageModuleRow
            key={m.id}
            module={m}
            checked={checkedModuleIds.has(m.id)}
            onToggle={(checked) =>
              setCheckedModuleIds((prev) => {
                const next = new Set(prev);
                if (checked) next.add(m.id);
                else next.delete(m.id);
                return next;
              })
            }
            onSaveEntitlement={onSaveEntitlement}
          />
        ))}
      </div>

      <div className="mt-4 flex justify-end">
        <Button disabled={!name || !price || create.isPending} onClick={() => create.mutate()}>
          <Plus className="h-4 w-4" aria-hidden />
          {create.isPending ? "Kreiranje..." : "Kreiraj paket"}
        </Button>
      </div>
    </div>
  );
}

function NewPackageModuleRow({
  module,
  checked,
  onToggle,
  onSaveEntitlement,
}: {
  module: ModuleDto;
  checked: boolean;
  onToggle: (checked: boolean) => void;
  onSaveEntitlement: (input: {
    moduleId: string;
    addonAvailable?: boolean;
    addonPrice?: string | null;
    minimumPackageId?: string | null; requiredModuleKeys?: string[];
  }) => void;
}) {
  const [addonAvailable, setAddonAvailable] = useState(module.addonAvailable);
  const [addonPrice, setAddonPrice] = useState(module.addonPrice ?? "");

  return (
    <div className="flex items-start gap-2 rounded-lg border border-border p-3">
      <input
        type="checkbox"
        checked={checked}
        onChange={(e) => onToggle(e.target.checked)}
        className="mt-0.5"
      />
      <div className="flex-1">
        <p className="text-sm font-medium text-slate-800">{module.name}</p>
        {module.description && <p className="text-xs text-slate-400">{module.description}</p>}
        <label className="mt-1.5 flex items-center gap-1.5 text-xs text-slate-500">
          <input
            type="checkbox"
            checked={addonAvailable}
            onChange={(e) => {
              setAddonAvailable(e.target.checked);
              onSaveEntitlement({ moduleId: module.id, addonAvailable: e.target.checked });
            }}
          />
          Dostupno kao addon
          <input
            value={addonPrice}
            onChange={(e) => setAddonPrice(e.target.value)}
            onBlur={() => onSaveEntitlement({ moduleId: module.id, addonPrice: addonPrice || null })}
            placeholder="Cena addon-a"
            disabled={!addonAvailable}
            className="h-7 w-24 rounded-md border border-border px-1.5 text-xs focus:border-brand focus:outline-none disabled:opacity-40"
          />
          <span className="text-slate-400">RSD</span>
        </label>
      </div>
    </div>
  );
}
